// JS for your graphic
import pym from "pym.js";
import * as topojson from "topojson";
import * as d3 from "d3";
import data from "../data/schools.csv";

class Map {
  // source = "./data/us_states.geo.json";
  source = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
  width = window.innerWidth - 5;
  height = this.width / 1.4;

  margin = { top: 10, right: 0, bottom: 0, left: 0 };
  sizeScale = d3.scaleSqrt().range([5, 30]);
  linearScale = d3.scaleLinear().range([5, 200]);

  constructor() {
    // in here, "this" is the Map instance
    // once we bind it, in draw, "this" will also be the Map instance
    this.chart = this.chart.bind(this);
    this.draw = this.draw.bind(this);
    this.loadData = this.loadData.bind(this);
  }

  resize() {
    this.width = window.innerWidth;
    this.height = this.width / 2;
    // this.projection.fitSize(
    //   [this.width * 0.9, this.height * 0.8],
    //   topojson.feature(this.topo, this.topo.objects.nation)
    // );
  }

  loadData() {
    return d3.json(this.source).then((topo) => {
      this.topo = topo;
      // don't have to specify projection for path because the topojson file we
      // load is preprojected with Albers
      // this is the projection that it uses (975x610)
      // this.projection = d3
      //   .geoIdentity()
      //   .fitSize(
      //     [this.width - 20, this.height],
      //     topojson.feature(this.topo, this.topo.objects.nation)
      //   );
      this.projection = d3.geoAlbersUsa();
      this.path = d3.geoPath(this.projection);
      return new Promise((resolve) => {
        resolve(this);
      });
    });
  }

  chart(datum, i, el) {
    const schoolNames = new Set(datum.map((d) => d.School));
    this.sizeScale.domain(d3.extent(datum.map((d) => d.apps)));
    this.linearScale.domain(d3.extent(datum.map((d) => d.apps)));
    this.projection.fitSize(
      [this.width, this.height * 0.8],
      topojson.feature(this.topo, this.topo.objects.nation)
    );
    this.path.projection(this.projection);
    const search = el
      .selectAll("div.search")
      .data([datum])
      .join("div")
      .attr("class", "search");

    search.html(
      (d) => `
      <input class="searchbar" list="schools" type="text" placeholder="Search for a high school"/>
      <datalist id="schools">
      ${d.map((v) => `<option value="${v.School}">`).join("\n")}
      </datalist>
      `
    );

    const svg = el
      .selectAll("svg")
      .data([datum])
      .join((enter) => enter.append("svg").call((enter) => enter.append("g")))
      .attr("width", this.width)
      .attr("height", this.height);

    const g = svg
      .select("g")
      .attr("transform", `translate(${this.margin.left}, ${this.margin.top})`);

    g.selectAll("path.map__interior")
      .data([topojson.feature(this.topo, this.topo.objects.nation)])
      .join("path")
      .attr("class", "map__interior")
      .attr("fill", "#DDD")
      .attr("d", this.path);

    g.selectAll("path.map__stateborder")
      .data([
        topojson.mesh(this.topo, this.topo.objects.states, (a, b) => a !== b),
      ])
      .join("path")
      .attr("class", "map__stateborder")
      .attr("d", this.path)
      .attr("fill", "none")
      .attr("stroke", "white");

    datum.forEach((d) => {
      d.proj = this.projection([d.lng, d.lat]);
    });

    g.selectAll("circle.map__school")
      .data(datum.filter((d) => !!(d.lng && d.lat)))
      .join("circle")
      .attr("class", "map__school")
      .attr("cx", (d) => d.proj[0])
      .attr("cy", (d) => d.proj[1])
      .attr("fill", "#374564")
      .attr("stroke", "#374564")
      .attr("r", (d) => this.sizeScale(d.apps));

    g.selectAll("text.map__school_text")
      .data(datum.filter((d) => !!(d.lng && d.lat)))
      .join("text")
      .attr("class", "map__school_text")
      .attr("dominant-baseline", "middle")
      .attr("x", (d) => d.proj[0])
      .attr("y", (d) => d.proj[1])
      .text((d) => d.apps);

    const circleLegend = function (selection) {
      // Praise be to:
      // https://bl.ocks.org/lorenzopub/90d90746af84f1fe3d782d47f3739a30
      let instance = {};

      // set some defaults
      const api = {
        domain: d3.extent(datum.map((d) => d.apps)), // the values min and max
        range: [5, 30], // the circle area/size mapping
        values: [30, 80, 160], // values for circles
        width: 2.5 * this.width / 2,
        height: this.height * 1.5,
        suffix: " applications", // ability to pass in a suffix
        circleColor: "#374564",
        textPadding: 10,
        textColor: "#454545",
      };

      const sqrtScale = d3.scaleSqrt().domain(api.domain).range(api.range);

      instance.render = function () {
        const s = selection
          .append("g")
          .attr("class", "legend-wrap")
          // push down to radius of largest circle
          .attr(
            "transform",
            "translate(0," + sqrtScale(d3.max(api.values)) + ")"
          );

        // append the values for circles
        s.append("g")
          .attr("class", "values-wrap")
          .selectAll("circle")
          .data(api.values)
          .enter()
          .append("circle")
          .attr("class", (d) => "values values-" + d)
          .attr("r", (d) => sqrtScale(d))
          .attr("cx", api.width / 2)
          .attr("cy", (d) => api.height / 2 - sqrtScale(d))
          .style("fill", "none")
          .style("stroke", api.circleColor)
          .style("opacity", 0.5);

        // append some lines based on values
        s.append("g")
          .attr("class", "values-line-wrap")
          .selectAll(".values-labels")
          .data(api.values)
          .enter()
          .append("line")
          .attr("x1", (d) => api.width / 2 + sqrtScale(d))
          .attr("x2", api.width / 2 + sqrtScale(api.domain[1]) + 10)
          .attr("y1", (d) => api.height / 2 - sqrtScale(d))
          .attr("y2", (d) => api.height / 2 - sqrtScale(d))
          .style("stroke", api.textColor)
          .style("stroke-dasharray", "2,2");

        // append some labels from values
        s.append("g")
          .attr("class", "values-labels-wrap")
          .selectAll(".values-labels")
          .data(api.values)
          .enter()
          .append("text")
          .attr("x", api.width / 2 + sqrtScale(api.domain[1]) + api.textPadding)
          .attr("y", (d) => api.height / 2 - sqrtScale(d) + 1)
          .attr("shape-rendering", "crispEdges")
          // .style("text-anchor", "end")
          .style("fill", api.textColor)
          .text((d, i) => i == 2 ? d + api.suffix : d);

        return instance;
      };

      for (let key in api) {
        instance[key] = getSet(key, instance).bind(api);
      }

      return instance;

      // https://gist.github.com/gneatgeek/5892586
      function getSet(option, component) {
        return function (_) {
          if (!arguments.length) {
            return this[option];
          }
          this[option] = _;
          return component;
        };
      }
    }.bind(this)

    const legend = g.append("g").attr("class", "legend")
    circleLegend(legend).render()

    // g.selectAll("rect.map__school")
    //   .data(datum.filter((d) => !!(d.lng && d.lat)))
    //   .join("rect")
    //   .attr("class", "map__school")
    //   .attr("x", (d) => d.proj[0])
    //   .attr("y", (d) => d.proj[1] - this.linearScale(d.apps))
    //   .attr("fill", "#37456420")
    //   .attr("stroke", "#374564")
    //   .attr("width", 3)
    //   .attr("height", d => this.linearScale(d.apps))
    //   // .attr("opacity", 0.1)
    //   // .attr("r", 3);
    //   // .attr("r", (d) => this.sizeScale(d.apps));

    // g.append("g")
    //   .attr("class", "brush")
    //   .call(
    //     d3.brush().on("end", ({selection}) => {
    //       let [[ax, ay], [bx, by]] = selection.map(this.projection.invert)
    //       console.log(ax, ay, bx, by)
    //       console.log(datum.map(d => [d.lng, d.lat]))
    //       console.log(datum.filter(d => d.lng > ax && d.lng < bx && d.lat > by && d.lat < ay))
    //     })
    //   );
    search.on("input", function (e, d) {
      const query = this.querySelector("input").value;
      if (schoolNames.has(query)) {
        g.selectAll("circle.map__school")
          .classed("hl", (d) => d.School == query)
          .classed("no-hl", (d) => d.School != query);
        g.selectAll("text.map__school_text").classed(
          "hl",
          (d) => d.School == query
        );
      } else {
        g.selectAll("circle.map__school")
          .classed("hl", false)
          .classed("no-hl", false);
      }
    });
  }

  draw(selection) {
    const chart = this.chart;
    selection.each(function (d, i) {
      // what is "this"?
      // "this" is the element in the selection
      chart(d, i, d3.select(this));
    });
  }
}

const loadData = () => {
  return d3.csv(data).then((d) => {
    d.forEach((d) => {
      d.aps = d["# of APs"];
      d.americanIndian = d["% American Indian"];
      d.lat = +d.Latitude;
      d.lng = +d.Longitude;
      d.apps = +d.Applications;
      d.admits = +d.Admitted;
      d.income = +d["Median Income"];
    });
    return new Promise((res) => res(d));
  });
};

const draw = (data) => {
  // step 1: access data
  // step 2: create chart dimensions
  // step 3: draw canvas
  // step 4: create scales
  // step 5: draw data
  // step 6: draw peripherals
  // step 7: set up interactions
  const map = new Map();
  map.loadData().then((map) => d3.select("figure").datum(data).call(map.draw));
};

const resize = () => {};

window.onload = function () {
  const pymChild = new pym.Child({ renderCallback: resize, polling: 500 });
  pymChild.sendHeight();

  loadData().then(draw);
};
